import { useState, useEffect, useCallback, useRef } from "react";
import API, { formatWIB, formatWIBDate, formatWIBTime } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  CurrencyDollar, Heartbeat, ChartLineUp, Warning, Gear, Robot,
  CheckCircle, ArrowClockwise, CaretLeft, CaretRight, Key, Power,
  Lightning, Trophy, Copy, Check, Broom, HardDrives, Trash,
} from "@phosphor-icons/react";

function fmtUSD(n, digits = 4) {
  if (n == null || isNaN(n)) return "$0";
  return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
function fmtInt(n) {
  if (n == null) return "0";
  return Number(n).toLocaleString("en-US");
}
function fmtPct(n, digits = 1) {
  if (n == null || isNaN(n)) return "0%";
  return (Number(n) * 100).toFixed(digits) + "%";
}
function fmtMs(n) {
  if (!n) return "0ms";
  if (n < 1000) return `${n}ms`;
  return `${(n / 1000).toFixed(2)}s`;
}

// Color for feature label
const FEATURE_COLOR = {
  deep_analyze: "bg-violet-100 text-violet-700 border-violet-200",
  ai_assistant: "bg-blue-100 text-blue-700 border-blue-200",
  briefing: "bg-emerald-100 text-emerald-700 border-emerald-200",
  suggestions: "bg-amber-100 text-amber-700 border-amber-200",
  job_summary: "bg-rose-100 text-rose-700 border-rose-200",
};

const STATUS_COLOR = {
  success: "bg-emerald-100 text-emerald-700",
  error: "bg-red-100 text-red-700",
  rate_limited: "bg-orange-100 text-orange-700",
  timeout: "bg-amber-100 text-amber-700",
  budget_blocked: "bg-zinc-200 text-zinc-700",
};

export default function AiAuditPage() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [features, setFeatures] = useState(null);
  const [quality, setQuality] = useState(null);
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState({ rows: [], total: 0 });
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBudget, setShowBudget] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ daily_cap_usd: 5, monthly_cap_usd: 100, hard_cap_enabled: true });
  const [savingBudget, setSavingBudget] = useState(false);

  // Logs filters
  const [logsFilters, setLogsFilters] = useState({ feature: "", status: "", days: 7, skip: 0 });
  const logsPerPage = 25;

  // AI Settings (provider / model / daily briefing toggle)
  const [aiSettings, setAiSettings] = useState(null);
  const [aiSettingsForm, setAiSettingsForm] = useState(null);
  const [savingAiSettings, setSavingAiSettings] = useState(false);

  // Log detail dialog
  const [logDetail, setLogDetail] = useState(null);
  const [loadingLogDetail, setLoadingLogDetail] = useState(false);

  // Benchmark state
  const [jobsList, setJobsList] = useState([]);
  const [benchForm, setBenchForm] = useState({ job_id: "", models: ["gpt-4o-mini", "claude-sonnet-4-5-20250929"] });
  const [benchRunning, setBenchRunning] = useState(false);
  const [benchmarks, setBenchmarks] = useState([]);
  const [activeBenchmark, setActiveBenchmark] = useState(null);
  const benchPollRef = useRef(null);

  // Maintenance state
  const [storage, setStorage] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState(null); // { key, label, count }
  const [purgeForm, setPurgeForm] = useState({ older_than_days: 30, feature: "", status: "" });
  const [purging, setPurging] = useState(false);
  // Auto-purge scheduler
  const [autoPurge, setAutoPurge] = useState(null); // { config, defaults, purgeable_collections }
  const [autoPurgeForm, setAutoPurgeForm] = useState(null);
  const [savingAutoPurge, setSavingAutoPurge] = useState(false);
  const [runningAutoPurge, setRunningAutoPurge] = useState(false);



  const openLogDetail = async (row) => {
    setLoadingLogDetail(true);
    setLogDetail({ _loading: true, ...row });
    try {
      const { data } = await API.get(`/ai-audit/logs/${row.id}`);
      setLogDetail(data);
    } catch (e) {
      setLogDetail({ error: e.response?.data?.detail || e.message, ...row });
    } finally {
      setLoadingLogDetail(false);
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ts, feat, ql, hl] = await Promise.all([
        API.get("/ai-audit/overview"),
        API.get("/ai-audit/cost-timeseries?days=30"),
        API.get("/ai-audit/features?days=7"),
        API.get("/ai-audit/quality?days=30"),
        API.get("/ai-audit/health?hours=24"),
      ]);
      setOverview(ov.data);
      setTimeseries(ts.data);
      setFeatures(feat.data);
      setQuality(ql.data);
      setHealth(hl.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (logsFilters.feature) params.set("feature", logsFilters.feature);
      if (logsFilters.status) params.set("status", logsFilters.status);
      params.set("days", logsFilters.days);
      params.set("skip", logsFilters.skip);
      params.set("limit", logsPerPage);
      const { data } = await API.get(`/ai-audit/logs?${params.toString()}`);
      setLogs(data);
    } catch (e) { console.error(e); }
  }, [logsFilters]);

  const loadBudget = useCallback(async () => {
    try {
      const { data } = await API.get("/ai-audit/budget");
      setBudget(data);
      setBudgetForm({
        daily_cap_usd: data.daily_cap_usd,
        monthly_cap_usd: data.monthly_cap_usd,
        hard_cap_enabled: data.hard_cap_enabled,
      });
    } catch (e) { console.error(e); }
  }, []);

  const loadAiSettings = useCallback(async () => {
    try {
      const { data } = await API.get("/ai-audit/settings");
      setAiSettings(data);
      // Initialize form from current settings. Use "__keep__" sentinel for API key
      setAiSettingsForm({
        daily_briefing_enabled: data.settings.daily_briefing_enabled,
        captcha_ai_enabled: !!data.settings.captcha_ai_enabled,
        ai_assistant_enabled: !!data.settings.ai_assistant_enabled,
        deep_analyze_enabled: !!data.settings.deep_analyze_enabled,
        job_summary_enabled: !!data.settings.job_summary_enabled,
        provider: data.settings.provider,
        custom_api_key: "__keep__",  // placeholder; user must type to change
        custom_base_url: data.settings.custom_base_url || "",
        default_model: data.settings.default_model,
        per_feature_model: { ...(data.settings.per_feature_model || {}) },
      });
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadAll(); loadBudget(); loadAiSettings(); }, [loadAll, loadBudget, loadAiSettings]);
  useEffect(() => { if (tab === "logs") loadLogs(); }, [tab, loadLogs]);

  // ─── Maintenance (storage stats + purge) ───
  const loadStorage = useCallback(async () => {
    setStorageLoading(true);
    try {
      const { data } = await API.get("/ai-audit/storage");
      setStorage(data);
    } catch (e) {
      console.error(e);
    } finally {
      setStorageLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === "maintenance") loadStorage(); }, [tab, loadStorage]);

  // Auto-purge config loader
  const loadAutoPurge = useCallback(async () => {
    try {
      const { data } = await API.get("/ai-audit/auto-purge");
      setAutoPurge(data);
      setAutoPurgeForm({
        enabled: !!data.config.enabled,
        interval_hours: data.config.interval_hours || 24,
        retention_days: { ...(data.config.retention_days || data.defaults) },
      });
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { if (tab === "maintenance") loadAutoPurge(); }, [tab, loadAutoPurge]);

  const saveAutoPurge = async () => {
    if (!autoPurgeForm) return;
    setSavingAutoPurge(true);
    try {
      await API.put("/ai-audit/auto-purge", {
        enabled: autoPurgeForm.enabled,
        interval_hours: Number(autoPurgeForm.interval_hours),
        retention_days: Object.fromEntries(
          Object.entries(autoPurgeForm.retention_days).map(([k, v]) => [k, Number(v)])
        ),
      });
      await loadAutoPurge();
      alert("Konfigurasi auto-purge tersimpan.");
    } catch (e) {
      alert("Gagal simpan: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingAutoPurge(false);
    }
  };

  const runAutoPurgeNow = async () => {
    if (!window.confirm("Jalankan auto-purge sekarang menggunakan konfigurasi tersimpan?")) return;
    setRunningAutoPurge(true);
    try {
      const { data } = await API.post("/ai-audit/auto-purge/run-now");
      if (!data.ran) {
        alert("Auto-purge tidak berjalan: " + (data.reason || "unknown"));
      } else {
        const lines = (data.summary || []).map(
          s => `- ${s.label}: ${s.skipped ? "skip" : `hapus ${s.deleted}, sisa ${s.remaining ?? "?"}`}`
        ).join("\n");
        alert(`Selesai. Total dihapus: ${data.total_deleted}\n\n${lines}`);
      }
      await loadAutoPurge();
      await loadStorage();
    } catch (e) {
      alert("Gagal run: " + (e.response?.data?.detail || e.message));
    } finally {
      setRunningAutoPurge(false);
    }
  };

  const openPurgeDialog = (coll) => {
    setPurgeTarget(coll);
    setPurgeForm({
      older_than_days: 30,
      feature: "",
      status: "",
    });
  };

  const runPurge = async () => {
    if (!purgeTarget) return;
    const typedName = window.prompt(
      `Konfirmasi penghapusan. Ketik nama koleksi di bawah persis untuk melanjutkan:\n\n${purgeTarget.collection}`
    );
    if (typedName !== purgeTarget.collection) {
      if (typedName !== null) {
        alert("Nama koleksi tidak cocok. Dibatalkan.");
      }
      return;
    }
    setPurging(true);
    try {
      const payload = { confirm: true };
      const days = Number(purgeForm.older_than_days);
      if (days > 0) payload.older_than_days = days;
      if (purgeTarget.key === "ai_call_logs") {
        if (purgeForm.feature) payload.feature = purgeForm.feature;
        if (purgeForm.status) payload.status = purgeForm.status;
      }
      const { data } = await API.post(`/ai-audit/purge/${purgeTarget.key}`, payload);
      alert(
        `Berhasil: ${data.deleted} dokumen dihapus dari ${data.label}.\nSisa: ${data.remaining} dokumen.`
      );
      setPurgeTarget(null);
      await loadStorage();
      // Also refresh logs if user kept the logs tab state
      if (tab === "logs") loadLogs();
      if (tab === "overview") loadAll();
    } catch (e) {
      alert("Gagal purge: " + (e.response?.data?.detail || e.message));
    } finally {
      setPurging(false);
    }
  };

  const saveAiSettings = async () => {
    setSavingAiSettings(true);
    try {
      const payload = { ...aiSettingsForm };
      await API.put("/ai-audit/settings", payload);
      await loadAiSettings();
      alert("Settings AI berhasil disimpan.");
    } catch (e) {
      alert("Gagal menyimpan settings: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingAiSettings(false);
    }
  };

  // ─── Benchmark ───

  const loadJobsForBenchmark = useCallback(async () => {
    try {
      const { data } = await API.get("/ai-audit/benchmark/candidate-jobs?limit=30");
      const jobs = data.jobs || [];
      setJobsList(jobs);
      // Auto-pick first eligible job
      const firstEligible = jobs.find(j => j.benchmark_eligible);
      if (!benchForm.job_id && firstEligible) {
        setBenchForm(f => ({ ...f, job_id: firstEligible.id }));
      }
    } catch (e) { console.error(e); }
  }, [benchForm.job_id]);

  const loadBenchmarks = useCallback(async () => {
    try {
      const { data } = await API.get("/ai-audit/benchmark?limit=10");
      setBenchmarks(data.benchmarks || []);
    } catch (e) { console.error(e); }
  }, []);

  const pollBenchmark = useCallback(async (bench_id) => {
    try {
      const { data } = await API.get(`/ai-audit/benchmark/${bench_id}`);
      setActiveBenchmark(data);
      if (data.status === "done" || data.status === "error") {
        if (benchPollRef.current) {
          clearInterval(benchPollRef.current);
          benchPollRef.current = null;
        }
        setBenchRunning(false);
        await loadBenchmarks();
      }
    } catch (e) { console.error(e); }
  }, [loadBenchmarks]);

  const runBenchmark = async () => {
    if (!benchForm.job_id || benchForm.models.length < 2) {
      alert("Pilih job dan minimal 2 model.");
      return;
    }
    setBenchRunning(true);
    setActiveBenchmark(null);
    try {
      const { data } = await API.post("/ai-audit/benchmark/run", benchForm);
      setActiveBenchmark(data);
      // Poll every 4s until done
      benchPollRef.current = setInterval(() => pollBenchmark(data.id), 4000);
    } catch (e) {
      // Friendly error display (e.g. "Job tidak punya pending items")
      setActiveBenchmark({ status: "error", error: e.response?.data?.detail || e.message });
      setBenchRunning(false);
    }
  };

  const toggleBenchmarkModel = (m) => {
    setBenchForm(f => {
      const has = f.models.includes(m);
      const models = has ? f.models.filter(x => x !== m) : [...f.models, m];
      return { ...f, models };
    });
  };

  useEffect(() => {
    if (tab === "benchmark") { loadJobsForBenchmark(); loadBenchmarks(); }
    return () => {
      if (benchPollRef.current) { clearInterval(benchPollRef.current); benchPollRef.current = null; }
    };
  }, [tab, loadJobsForBenchmark, loadBenchmarks]);

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      await API.put("/ai-audit/budget", budgetForm);
      setShowBudget(false);
      await loadBudget();
      await loadAll();
    } catch (e) {
      alert("Gagal menyimpan budget: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingBudget(false);
    }
  };

  const TABS = [
    { k: "overview", label: "Cost & Usage", icon: CurrencyDollar },
    { k: "quality", label: "Quality", icon: ChartLineUp },
    { k: "health", label: "Health & Errors", icon: Heartbeat },
    { k: "features", label: "Per-Feature", icon: Robot },
    { k: "logs", label: "Raw Logs", icon: Warning },
    { k: "benchmark", label: "Benchmark", icon: Lightning },
    { k: "settings", label: "Settings", icon: Key },
    { k: "maintenance", label: "Maintenance", icon: Broom },
  ];

  return (
    <div data-testid="ai-audit-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">AI Audit & Maintenance</h1>
          <p className="text-sm text-zinc-500 mt-1">Observability & biaya untuk seluruh fitur AI di platform</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="refresh-audit-btn" variant="outline" onClick={loadAll} className="rounded-sm gap-1 text-xs h-9">
            <ArrowClockwise size={14} /> Refresh
          </Button>
          <Button data-testid="open-budget-btn" onClick={() => setShowBudget(true)} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-1 text-xs h-9">
            <Gear size={14} /> Budget Config
          </Button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-zinc-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.k;
          return (
            <button
              key={t.k}
              data-testid={`audit-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                active ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}>
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && <div className="p-8 text-center text-sm text-zinc-400">Loading...</div>}

      {/* OVERVIEW / COST & USAGE */}
      {tab === "overview" && overview && (
        <div className="space-y-4">
          {/* Top KPI cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <KpiCard
              testId="kpi-today-cost"
              label="Biaya Hari Ini"
              value={fmtUSD(overview.today.cost_usd, 4)}
              sub={`${fmtInt(overview.today.calls)} calls · avg ${fmtMs(overview.today.avg_latency_ms)}`}
              accent="zinc"
            />
            <KpiCard
              testId="kpi-month-cost"
              label="Biaya Bulan Ini"
              value={fmtUSD(overview.month.cost_usd, 4)}
              sub={`${fmtInt(overview.month.calls)} calls`}
              accent="violet"
            />
            <KpiCard
              testId="kpi-budget-daily"
              label="Budget Harian"
              value={fmtUSD(overview.budget.daily_cap_usd, 2)}
              sub={`Terpakai ${overview.budget.daily_pct}% ${overview.budget.hard_cap_enabled ? "· hard cap ON" : "· soft only"}`}
              accent={overview.budget.daily_pct >= 80 ? "red" : overview.budget.daily_pct >= 50 ? "amber" : "emerald"}
              progress={overview.budget.daily_pct}
            />
            <KpiCard
              testId="kpi-cache-hit"
              label="Cache Hit Rate (30d)"
              value={fmtPct(overview.cache_hit_rate)}
              sub={`${fmtInt(overview.cache_hits_month)} hits · ${fmtInt(overview.cache_miss_month)} miss`}
              accent="blue"
            />
          </div>

          {/* Monthly budget bar */}
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600">Monthly Budget Utilization</h3>
              <span className="text-xs font-mono text-zinc-500">
                {fmtUSD(overview.month.cost_usd, 4)} / {fmtUSD(overview.budget.monthly_cap_usd, 2)} ({overview.budget.monthly_pct}%)
              </span>
            </div>
            <div className="h-3 rounded-sm bg-zinc-100 overflow-hidden">
              <div
                data-testid="monthly-budget-bar"
                className={`h-full transition-all ${overview.budget.monthly_pct >= 80 ? "bg-red-500" : overview.budget.monthly_pct >= 50 ? "bg-amber-500" : "bg-emerald-500"}`}
                style={{ width: `${Math.min(100, overview.budget.monthly_pct)}%` }}
              />
            </div>
          </div>

          {/* Cost time-series chart */}
          {timeseries && <CostTimeSeriesChart data={timeseries} />}

          {/* Token usage summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-zinc-200 rounded-sm p-4">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600 mb-3">Token Usage (Hari Ini)</h3>
              <div className="space-y-2">
                <Row label="Input tokens" value={fmtInt(overview.today.input_tokens)} />
                <Row label="Output tokens" value={fmtInt(overview.today.output_tokens)} />
                <Row label="Total" value={fmtInt(overview.today.input_tokens + overview.today.output_tokens)} bold />
              </div>
            </div>
            <div className="border border-zinc-200 rounded-sm p-4">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600 mb-3">Token Usage (Bulan Ini)</h3>
              <div className="space-y-2">
                <Row label="Input tokens" value={fmtInt(overview.month.input_tokens)} />
                <Row label="Output tokens" value={fmtInt(overview.month.output_tokens)} />
                <Row label="Total" value={fmtInt(overview.month.input_tokens + overview.month.output_tokens)} bold />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QUALITY */}
      {tab === "quality" && quality && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <KpiCard testId="kpi-generated" label="Verdicts Generated (30d)" value={fmtInt(quality.total_generated)} sub="Seluruh deep analyze" accent="violet" />
            <KpiCard testId="kpi-applied" label="Verdicts Applied" value={fmtInt(quality.total_applied)} sub="Di-apply oleh operator" accent="emerald" />
            <KpiCard testId="kpi-apply-rate" label="Apply Rate" value={fmtPct(quality.apply_rate)} sub="target > 60%" accent={quality.apply_rate >= 0.6 ? "emerald" : quality.apply_rate >= 0.3 ? "amber" : "red"} />
            <KpiCard testId="kpi-confidence" label="Avg Confidence" value={quality.avg_confidence.toFixed(2)} sub="Rata-rata confidence AI" accent="blue" />
          </div>

          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600">Breakdown per Bucket (30 hari)</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Bucket</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Generated</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Match Found</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">No Match</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Needs Review</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Applied</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Apply Rate</th>
                </tr>
              </thead>
              <tbody>
                {quality.per_bucket.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-zinc-400">Belum ada verdict dihasilkan.</td></tr>
                ) : quality.per_bucket.map((b) => (
                  <tr key={b.bucket} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-3 py-2 text-xs font-mono font-semibold text-zinc-900">{b.bucket}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right">{b.generated}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right text-emerald-700">{b.match_found}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right text-zinc-500">{b.no_match}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right text-amber-700">{b.needs_review}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right font-semibold">{b.applied}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right">
                      <span className={`px-1.5 py-0.5 rounded-sm ${b.apply_rate >= 0.6 ? "bg-emerald-100 text-emerald-700" : b.apply_rate >= 0.3 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {fmtPct(b.apply_rate)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-blue-200 bg-blue-50 rounded-sm p-3 text-[11px] text-blue-900">
            <strong>Catatan:</strong> Apply Rate mengindikasikan berapa banyak verdict AI yang benar-benar diverifikasi & dieksekusi operator. Target idealnya &gt;60% — artinya AI benar-benar membantu. Jika rendah (&lt;30%), mungkin prompt perlu di-tuning atau confidence threshold terlalu agresif.
          </div>
        </div>
      )}

      {/* HEALTH */}
      {tab === "health" && health && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <KpiCard testId="kpi-total-calls-24h" label="Total Calls (24h)" value={fmtInt(health.total_calls)} sub="Seluruh fitur AI" accent="zinc" />
            <KpiCard testId="kpi-error-rate" label="Error Rate" value={fmtPct(health.error_rate, 2)} sub={`${Object.values(health.errors_by_type).reduce((a,b)=>a+b,0)} errors`} accent={health.error_rate >= 0.05 ? "red" : health.error_rate >= 0.01 ? "amber" : "emerald"} />
            <KpiCard testId="kpi-p50-latency" label="Latency p50" value={fmtMs(health.latency_p50_ms)} sub="Median response time" accent="blue" />
            <KpiCard testId="kpi-p95-latency" label="Latency p95" value={fmtMs(health.latency_p95_ms)} sub="threshold 10s" accent={health.latency_p95_ms > 10000 ? "red" : health.latency_p95_ms > 5000 ? "amber" : "emerald"} />
          </div>

          {/* Errors by type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-zinc-200 rounded-sm p-4">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600 mb-3">Error Breakdown (24h)</h3>
              {Object.keys(health.errors_by_type).length === 0 ? (
                <p className="text-xs text-emerald-600 font-mono flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Tidak ada error dalam 24 jam terakhir</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(health.errors_by_type).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between">
                      <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm ${STATUS_COLOR[k] || "bg-zinc-100 text-zinc-700"}`}>{k}</span>
                      <span className="text-xs font-mono font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Prompt version distribution */}
            <div className="border border-zinc-200 rounded-sm p-4">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600 mb-3">Prompt Versions (Deep Analyze)</h3>
              {health.prompt_versions.length === 0 ? (
                <p className="text-xs text-zinc-400 font-mono">Belum ada data</p>
              ) : (
                <div className="space-y-2">
                  {health.prompt_versions.map((p) => (
                    <div key={p.version} className="flex items-center justify-between">
                      <span className="text-xs font-mono text-zinc-700">{p.version}</span>
                      <span className="text-xs font-mono font-semibold">{p.count} calls</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent errors list */}
          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600">Recent Errors (Latest 10)</h3>
            </div>
            {health.recent_errors.length === 0 ? (
              <div className="p-8 text-center text-xs text-emerald-600 font-mono flex items-center justify-center gap-1">
                <CheckCircle size={14} weight="fill" /> Tidak ada error baru
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Waktu</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Feature</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Status</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {health.recent_errors.map((e, i) => (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="px-3 py-2 text-[10px] font-mono text-zinc-500">{formatWIB(e.created_at)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm border ${FEATURE_COLOR[e.feature] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>{e.feature}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm ${STATUS_COLOR[e.status] || "bg-zinc-100 text-zinc-700"}`}>{e.status}</span>
                      </td>
                      <td className="px-3 py-2 text-[10px] text-red-700 max-w-[400px] truncate" title={e.error}>{e.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* PER-FEATURE */}
      {tab === "features" && features && (
        <div className="space-y-4">
          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600">Per-Feature Breakdown ({features.days} hari)</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Feature</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Calls</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Success</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Errors</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Error %</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Cost</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Avg Tokens</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Avg Latency</th>
                </tr>
              </thead>
              <tbody>
                {features.features.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-xs text-zinc-400">Belum ada aktivitas AI dalam {features.days} hari terakhir</td></tr>
                ) : features.features.map((f) => (
                  <tr key={f.feature} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${FEATURE_COLOR[f.feature] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>{f.feature}</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-right">{f.calls}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right text-emerald-700">{f.success}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right text-red-700">{f.errors}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right">
                      <span className={`px-1.5 py-0.5 rounded-sm ${f.error_rate >= 0.05 ? "bg-red-100 text-red-700" : f.error_rate >= 0.01 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {fmtPct(f.error_rate, 2)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-right font-semibold">{fmtUSD(f.cost_usd, 4)}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right text-zinc-500">{fmtInt(f.avg_tokens)}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right text-zinc-500">{fmtMs(f.avg_latency_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* RAW LOGS */}
      {tab === "logs" && (
        <div className="space-y-3">
          <div className="border border-zinc-200 rounded-sm p-3 flex items-center gap-2 flex-wrap">
            <select data-testid="logs-feature-filter" value={logsFilters.feature}
              onChange={(e) => setLogsFilters(s => ({ ...s, feature: e.target.value, skip: 0 }))}
              className="border border-zinc-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
              <option value="">Semua Feature</option>
              <option value="deep_analyze">deep_analyze</option>
              <option value="ai_assistant">ai_assistant</option>
              <option value="briefing">briefing</option>
              <option value="suggestions">suggestions</option>
              <option value="job_summary">job_summary</option>
            </select>
            <select data-testid="logs-status-filter" value={logsFilters.status}
              onChange={(e) => setLogsFilters(s => ({ ...s, status: e.target.value, skip: 0 }))}
              className="border border-zinc-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
              <option value="">Semua Status</option>
              <option value="success">success</option>
              <option value="error">error</option>
              <option value="rate_limited">rate_limited</option>
              <option value="timeout">timeout</option>
              <option value="budget_blocked">budget_blocked</option>
            </select>
            <select data-testid="logs-days-filter" value={logsFilters.days}
              onChange={(e) => setLogsFilters(s => ({ ...s, days: Number(e.target.value), skip: 0 }))}
              className="border border-zinc-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
              <option value="1">1 hari</option>
              <option value="7">7 hari</option>
              <option value="30">30 hari</option>
              <option value="90">90 hari</option>
            </select>
            <span className="text-[10px] font-mono text-zinc-500 ml-auto">{fmtInt(logs.total)} total</span>
          </div>

          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Waktu</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Feature</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Model</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Tokens</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Latency</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Cost</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Job / Error</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {logs.rows.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-xs text-zinc-400">Tidak ada log</td></tr>
                ) : logs.rows.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-3 py-2 text-[10px] font-mono text-zinc-500">{formatWIB(r.created_at)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm border ${FEATURE_COLOR[r.feature] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>{r.feature}</span>
                    </td>
                    <td className="px-3 py-2 text-[10px] font-mono text-zinc-600">{r.model}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm ${STATUS_COLOR[r.status] || "bg-zinc-100 text-zinc-700"}`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-right">{fmtInt(r.total_tokens)}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right text-zinc-500">{fmtMs(r.latency_ms)}</td>
                    <td className="px-3 py-2 text-xs font-mono text-right font-semibold">{fmtUSD(r.cost_usd, 6)}</td>
                    <td className="px-3 py-2 text-[10px] text-zinc-500 max-w-[240px] truncate">
                      {r.error ? <span className="text-red-600">{r.error}</span> : (r.job_id ? <span className="font-mono">{r.job_id?.slice(0, 8)}</span> : "-")}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.feature !== "suggestions" && (
                        <button
                          data-testid={`log-detail-btn-${r.id}`}
                          onClick={() => openLogDetail(r)}
                          className="text-[10px] text-violet-600 hover:text-violet-800 underline font-mono">
                          Lihat
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {logs.total > logsPerPage && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-zinc-500 font-mono">
                {logsFilters.skip + 1}-{Math.min(logsFilters.skip + logsPerPage, logs.total)} dari {logs.total}
              </p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm"
                  onClick={() => setLogsFilters(s => ({ ...s, skip: Math.max(0, s.skip - logsPerPage) }))}
                  disabled={logsFilters.skip <= 0}
                  className="rounded-sm h-7 w-7 p-0"><CaretLeft size={14} /></Button>
                <Button variant="outline" size="sm"
                  onClick={() => setLogsFilters(s => ({ ...s, skip: s.skip + logsPerPage }))}
                  disabled={logsFilters.skip + logsPerPage >= logs.total}
                  className="rounded-sm h-7 w-7 p-0"><CaretRight size={14} /></Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* BENCHMARK */}
      {tab === "benchmark" && (
        <div className="space-y-4">
          {/* Run form */}
          <div className="border border-zinc-200 rounded-sm p-4 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                  <Lightning size={16} weight="fill" className="text-amber-500" /> Run Benchmark
                </h3>
                <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
                  Bandingkan 2-4 model LLM untuk fitur <strong>Deep Analyze</strong> pada job yang sama.
                  Sistem akan menjalankan analisis dengan tiap model secara paralel lalu tampilkan perbandingan biaya, akurasi (agreement rate), dan latency.
                  <span className="block mt-1 text-amber-600">
                    ⚠ Benchmark membutuhkan API call nyata — biaya akan tercatat di ledger.
                  </span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Pilih Job</Label>
                <select
                  data-testid="bench-job-select"
                  value={benchForm.job_id}
                  onChange={(e) => setBenchForm(f => ({ ...f, job_id: e.target.value }))}
                  className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-xs bg-white outline-none font-mono">
                  <option value="">— Pilih job —</option>
                  {(() => {
                    const eligible = jobsList.filter(j => j.benchmark_eligible);
                    const ineligible = jobsList.filter(j => !j.benchmark_eligible);
                    return (
                      <>
                        {eligible.length > 0 && (
                          <optgroup label={`✓ Layak Benchmark (${eligible.length})`}>
                            {eligible.map(j => (
                              <option key={j.id} value={j.id}>
                                {j.site_name || "?"} · {j.date_from} → {j.date_to} · {j.needle_count} needles · {j.id?.slice(0, 8)}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {ineligible.length > 0 && (
                          <optgroup label={`— Tidak Layak (${ineligible.length}) —`}>
                            {ineligible.map(j => (
                              <option key={j.id} value={j.id} disabled>
                                {j.site_name || "?"} · {j.date_from} → {j.date_to} · {j.needle_count} needles · {j.id?.slice(0, 8)}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </>
                    );
                  })()}
                </select>
                <p className="text-[10px] text-zinc-400">
                  {jobsList.filter(j => j.benchmark_eligible).length > 0
                    ? `${jobsList.filter(j => j.benchmark_eligible).length} job siap di-benchmark. Sorted by needle count.`
                    : "Belum ada job dengan pending items. Jalankan job baru atau reset verifikasi existing."}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
                  Model ({benchForm.models.length}/4)
                </Label>
                <div className="flex flex-wrap gap-1">
                  {[
                    "gpt-4o-mini", "gpt-4o", "gpt-5-mini",
                    "claude-haiku-4-5-20251001", "claude-sonnet-4-5-20250929", "claude-opus-4-5-20251001",
                    "gemini-2.5-flash", "gemini-2.5-pro",
                  ].map(m => {
                    const active = benchForm.models.includes(m);
                    return (
                      <button
                        key={m}
                        data-testid={`bench-model-${m}`}
                        onClick={() => toggleBenchmarkModel(m)}
                        disabled={!active && benchForm.models.length >= 4}
                        className={`text-[10px] font-mono px-2 py-1 rounded-sm border transition-colors ${
                          active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"
                        } ${!active && benchForm.models.length >= 4 ? "opacity-40 cursor-not-allowed" : ""}`}>
                        {m}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-zinc-400">Pilih 2-4 model untuk dibandingkan.</p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                data-testid="bench-run-btn"
                onClick={runBenchmark}
                disabled={benchRunning || !benchForm.job_id || benchForm.models.length < 2}
                className="bg-amber-500 text-white hover:bg-amber-600 rounded-sm gap-1 text-xs h-9">
                <Lightning size={14} weight="fill" />
                {benchRunning ? "Running..." : "Run Benchmark"}
              </Button>
            </div>
          </div>

          {/* Active benchmark result */}
          {activeBenchmark && <BenchmarkResult benchmark={activeBenchmark} />}

          {/* History */}
          {benchmarks.length > 0 && (
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600">Benchmark History (10 terakhir)</h3>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Waktu</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Job</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Models</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Winner</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Cost</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Agreement</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {benchmarks.map((b) => (
                    <tr key={b.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-3 py-2 text-[10px] font-mono text-zinc-500">{formatWIB(b.created_at)}</td>
                      <td className="px-3 py-2 text-[10px] font-mono text-zinc-600">{b.job_id?.slice(0, 8)}</td>
                      <td className="px-3 py-2 text-[10px] font-mono text-zinc-600">{(b.models || []).join(", ")}</td>
                      <td className="px-3 py-2 text-[10px] font-mono">
                        {b.winner ? <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-sm inline-flex items-center gap-1"><Trophy size={10} weight="fill" /> {b.winner}</span> : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-right font-semibold">{fmtUSD(b.total_cost_usd, 4)}</td>
                      <td className="px-3 py-2 text-xs font-mono text-right">
                        {b.agreement_summary?.needles ? fmtPct(b.agreement_summary.rate, 0) : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm ${
                          b.status === "done" ? "bg-emerald-100 text-emerald-700"
                          : b.status === "running" ? "bg-blue-100 text-blue-700"
                          : b.status === "error" ? "bg-red-100 text-red-700"
                          : "bg-zinc-100 text-zinc-700"
                        }`}>{b.status}</span>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          data-testid={`bench-view-${b.id}`}
                          onClick={async () => {
                            try {
                              const { data } = await API.get(`/ai-audit/benchmark/${b.id}`);
                              setActiveBenchmark(data);
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            } catch (e) { console.error(e); }
                          }}
                          className="text-[10px] text-violet-600 hover:text-violet-800 underline">
                          Lihat
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {benchmarks.length === 0 && !activeBenchmark && (
            <div className="border border-dashed border-zinc-300 rounded-sm p-8 text-center text-xs text-zinc-400">
              Belum ada benchmark. Jalankan benchmark pertama Anda di atas.
            </div>
          )}
        </div>
      )}

      {/* SETTINGS */}
      {tab === "settings" && aiSettings && aiSettingsForm && (
        <div className="space-y-4">
          {/* Daily Briefing toggle */}
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                  <Power size={16} /> Daily Briefing
                </h3>
                <p className="text-xs text-zinc-500 mt-1 max-w-xl">
                  Bila dinonaktifkan, AI tidak akan meng-generate briefing pagi otomatis di Dashboard.
                  Briefing yang sudah tersimpan (cache hari ini) masih bisa dibaca, tapi tidak akan di-refresh.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="setting-briefing-toggle"
                  type="checkbox"
                  checked={aiSettingsForm.daily_briefing_enabled}
                  onChange={(e) => setAiSettingsForm(s => ({ ...s, daily_briefing_enabled: e.target.checked }))}
                  className="w-4 h-4"
                />
                <span className={`text-xs font-semibold ${aiSettingsForm.daily_briefing_enabled ? "text-emerald-600" : "text-zinc-400"}`}>
                  {aiSettingsForm.daily_briefing_enabled ? "AKTIF" : "NONAKTIF"}
                </span>
              </label>
            </div>
          </div>

          {/* AI Feature Toggles — per-feature on/off for self-hosted deployments */}
          <div className="border border-zinc-200 rounded-sm p-4 space-y-3" data-testid="ai-feature-toggles">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <Power size={16} /> AI Feature Toggles
              </h3>
              <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
                Aktifkan/nonaktifkan setiap fitur AI secara terpisah. Default <strong>NONAKTIF</strong> untuk
                self-hosted deployment tanpa LLM API key. Setelah toggle AKTIF, sistem akan menggunakan
                provider & model yang dikonfigurasi di bawah ini.
              </p>
            </div>
            {[
              { k: "captcha_ai_enabled", label: "AI Vision CAPTCHA", desc: "Otomatis solve CAPTCHA panel pakai model vision. Jika NONAKTIF, sistem langsung minta input manual ke admin." },
              { k: "job_summary_enabled", label: "Risk Score & Job Summary", desc: "AI generate ringkasan otomatis & risk score di akhir setiap job rekonsiliasi." },
              { k: "deep_analyze_enabled", label: "Deep Analysis", desc: "Re-validasi item yang gagal cocok pakai AI deep search (tombol Deep Analyze di Review Queue)." },
              { k: "ai_assistant_enabled", label: "AI Assistant (Chatbot)", desc: "Chatbot AI dengan function calling, daily briefing, dan suggestions di Dashboard." },
            ].map((feat) => (
              <div key={feat.k} className="flex items-start justify-between gap-4 py-3 border-t border-zinc-100 first:border-t-0 first:pt-0">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-zinc-800">{feat.label}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{feat.desc}</p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
                  <input
                    data-testid={`setting-toggle-${feat.k}`}
                    type="checkbox"
                    checked={!!aiSettingsForm[feat.k]}
                    onChange={(e) => setAiSettingsForm(s => ({ ...s, [feat.k]: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <span className={`text-xs font-semibold ${aiSettingsForm[feat.k] ? "text-emerald-600" : "text-zinc-400"}`}>
                    {aiSettingsForm[feat.k] ? "AKTIF" : "NONAKTIF"}
                  </span>
                </label>
              </div>
            ))}
          </div>

          {/* Provider selector */}
          <div className="border border-zinc-200 rounded-sm p-4 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <Key size={16} /> LLM Provider
              </h3>
              <p className="text-xs text-zinc-500 mt-1">
                Pilih sumber API untuk semua panggilan AI. <strong>Emergent LLM Key</strong> (default) mendukung semua model (GPT/Claude/Gemini) dengan 1 kredensial.
                Custom key berguna jika Anda ingin menggunakan akun sendiri atau mendapat harga khusus dari provider.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { k: "emergent", label: "Emergent LLM Key", desc: "Default, multi-provider" },
                { k: "openai", label: "OpenAI", desc: "GPT family" },
                { k: "anthropic", label: "Anthropic", desc: "Claude family" },
                { k: "gemini", label: "Google Gemini", desc: "Gemini family" },
              ].map(p => {
                const active = aiSettingsForm.provider === p.k;
                return (
                  <button
                    key={p.k}
                    data-testid={`setting-provider-${p.k}`}
                    onClick={() => setAiSettingsForm(s => ({ ...s, provider: p.k }))}
                    className={`text-left p-3 rounded-sm border transition-colors ${
                      active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white hover:bg-zinc-50"
                    }`}>
                    <p className="text-xs font-semibold">{p.label}</p>
                    <p className={`text-[10px] mt-0.5 ${active ? "text-zinc-300" : "text-zinc-500"}`}>{p.desc}</p>
                  </button>
                );
              })}
            </div>

            {/* Custom key fields — only when non-emergent */}
            {aiSettingsForm.provider !== "emergent" && (
              <div className="space-y-3 pt-2 border-t border-zinc-100">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
                    API Key {aiSettings.settings.custom_api_key_masked && (
                      <span className="font-mono text-zinc-400 normal-case tracking-normal ml-2">
                        (saat ini: {aiSettings.settings.custom_api_key_masked})
                      </span>
                    )}
                  </Label>
                  <Input
                    data-testid="setting-custom-api-key"
                    type="password"
                    placeholder={aiSettings.settings.custom_api_key_masked ? "Kosongkan = gunakan yang sudah tersimpan" : "sk-... / anthropic_... / AIza..."}
                    value={aiSettingsForm.custom_api_key === "__keep__" ? "" : aiSettingsForm.custom_api_key}
                    onChange={(e) => setAiSettingsForm(s => ({ ...s, custom_api_key: e.target.value }))}
                    className="rounded-sm border-zinc-300 font-mono text-xs"
                  />
                  <p className="text-[10px] text-zinc-400">Key akan disimpan terenkripsi (base64). Kosongkan form & klik Simpan untuk tetap memakai key yang sudah ada.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Base URL (opsional)</Label>
                  <Input
                    data-testid="setting-custom-base-url"
                    placeholder="https://api.openai.com/v1 (biarkan kosong = default)"
                    value={aiSettingsForm.custom_base_url}
                    onChange={(e) => setAiSettingsForm(s => ({ ...s, custom_base_url: e.target.value }))}
                    className="rounded-sm border-zinc-300 font-mono text-xs"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Model selection */}
          <div className="border border-zinc-200 rounded-sm p-4 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <Robot size={16} /> Model Configuration
              </h3>
              <p className="text-xs text-zinc-500 mt-1">
                Default model dipakai untuk semua fitur AI. Anda bisa override per-fitur (misal pakai Claude Sonnet untuk Deep Analyze, GPT-4o-mini untuk Assistant) untuk optimasi biaya/kualitas.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Default Model (global)</Label>
              <ModelSelect
                testId="setting-default-model"
                value={aiSettingsForm.default_model}
                onChange={(v) => setAiSettingsForm(s => ({ ...s, default_model: v }))}
                catalog={aiSettings.model_catalog}
              />
            </div>

            <div className="pt-2 border-t border-zinc-100 space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Per-Feature Override</p>
              <p className="text-[10px] text-zinc-400 -mt-2">Pilih "Gunakan default" agar fitur ini mengikuti Default Model di atas.</p>
              {aiSettings.features.map(f => (
                <div key={f} className="grid grid-cols-3 items-center gap-3">
                  <Label className="text-xs font-mono text-zinc-700">{f}</Label>
                  <div className="col-span-2">
                    <ModelSelect
                      testId={`setting-feature-${f}`}
                      value={aiSettingsForm.per_feature_model[f]}
                      onChange={(v) => setAiSettingsForm(s => ({
                        ...s,
                        per_feature_model: { ...s.per_feature_model, [f]: v }
                      }))}
                      catalog={aiSettings.model_catalog}
                      allowDefault
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3">
            <Button variant="outline" onClick={loadAiSettings} disabled={savingAiSettings}
              className="rounded-sm gap-1 text-xs h-9">
              <ArrowClockwise size={14} /> Reset
            </Button>
            <Button data-testid="save-ai-settings-btn" onClick={saveAiSettings} disabled={savingAiSettings}
              className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-1 text-xs h-9">
              {savingAiSettings ? "Menyimpan..." : "Simpan Settings"}
            </Button>
          </div>

          {/* Last updated info */}
          {aiSettings.settings.updated_at && (
            <p className="text-[10px] text-zinc-400 font-mono text-right">
              Terakhir diupdate: {formatWIB(aiSettings.settings.updated_at)}
            </p>
          )}
        </div>
      )}

      {/* MAINTENANCE — Storage stats & Purge / Reset */}
      {tab === "maintenance" && (
        <div data-testid="maintenance-panel" className="space-y-4">
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                  <HardDrives size={16} weight="fill" className="text-zinc-500" />
                  Storage & Log Maintenance
                </h3>
                <p className="text-[11px] text-zinc-500 mt-1">
                  Reset / hapus log AI, benchmark, dan system log jika storage mulai penuh.
                  TTL otomatis 90 hari sudah aktif untuk AI Call Logs, tapi reset manual bisa
                  dipakai untuk pembersihan cepat.
                </p>
              </div>
              <Button
                data-testid="maintenance-refresh-btn"
                variant="outline"
                onClick={loadStorage}
                disabled={storageLoading}
                className="rounded-sm gap-1 text-xs h-8">
                <ArrowClockwise size={14} /> {storageLoading ? "..." : "Refresh"}
              </Button>
            </div>

            {storage && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Docs</div>
                  <div className="text-xl font-mono font-semibold text-zinc-900 mt-1">
                    {fmtInt(storage.total_docs)}
                  </div>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Size</div>
                  <div className="text-xl font-mono font-semibold text-zinc-900 mt-1">
                    {storage.total_size_mb} MB
                  </div>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Koleksi</div>
                  <div className="text-xl font-mono font-semibold text-zinc-900 mt-1">
                    {storage.collections?.length || 0}
                  </div>
                </div>
              </div>
            )}
          </div>

          {storage && (
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Koleksi</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Jumlah</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Ukuran</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Avg / Doc</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Tertua</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Terbaru</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {storage.collections?.map((c) => (
                    <tr key={c.key} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-3 py-2">
                        <div className="text-xs font-semibold text-zinc-900">{c.label}</div>
                        <div className="text-[10px] font-mono text-zinc-400">{c.collection}</div>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-right">{fmtInt(c.count)}</td>
                      <td className="px-3 py-2 text-xs font-mono text-right font-semibold">
                        {c.size_mb < 0.01 ? "< 0.01 MB" : `${c.size_mb} MB`}
                      </td>
                      <td className="px-3 py-2 text-[10px] font-mono text-right text-zinc-500">
                        {c.avg_size_bytes ? `${c.avg_size_bytes} B` : "-"}
                      </td>
                      <td className="px-3 py-2 text-[10px] font-mono text-zinc-500">
                        {c.oldest ? formatWIBDate(c.oldest) : "-"}
                      </td>
                      <td className="px-3 py-2 text-[10px] font-mono text-zinc-500">
                        {c.newest ? formatWIBDate(c.newest) : "-"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          data-testid={`purge-btn-${c.key}`}
                          size="sm"
                          variant="outline"
                          disabled={c.count === 0}
                          onClick={() => openPurgeDialog(c)}
                          className="rounded-sm gap-1 text-[10px] h-7 px-2 border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40">
                          <Trash size={12} /> Reset
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-[11px] text-amber-800">
            <strong>Catatan keamanan:</strong> Tombol Reset bersifat destruktif. Sistem akan
            meminta Anda mengetik ulang nama koleksi sebelum menghapus. TTL index otomatis
            membersihkan <code className="font-mono text-[10px] bg-white px-1 py-0.5 rounded-sm">ai_call_logs</code> setelah
            90 hari — reset manual hanya untuk keadaan darurat (storage penuh).
          </div>

          {/* Auto-Purge Scheduler */}
          {autoPurge && autoPurgeForm && (
            <div data-testid="auto-purge-panel" className="border border-zinc-200 rounded-sm p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                    <Power size={16} weight="fill" className={autoPurgeForm.enabled ? "text-emerald-500" : "text-zinc-400"} />
                    Auto-Purge Scheduler
                  </h3>
                  <p className="text-[11px] text-zinc-500 mt-1">
                    Bersihkan log & benchmark otomatis setiap N jam tanpa perlu reset manual.
                    Cocok untuk server VPS produksi agar storage tetap stabil.
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-zinc-700">
                  <input
                    data-testid="auto-purge-enabled-toggle"
                    type="checkbox"
                    checked={autoPurgeForm.enabled}
                    onChange={(e) => setAutoPurgeForm(f => ({ ...f, enabled: e.target.checked }))}
                    className="w-4 h-4 accent-emerald-600"
                  />
                  {autoPurgeForm.enabled ? "Aktif" : "Nonaktif"}
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-zinc-600">Interval (jam antar run)</Label>
                  <Input
                    data-testid="auto-purge-interval-input"
                    type="number"
                    min="1"
                    max="720"
                    value={autoPurgeForm.interval_hours}
                    onChange={(e) => setAutoPurgeForm(f => ({ ...f, interval_hours: e.target.value }))}
                    className="mt-1 h-8 text-xs font-mono rounded-sm"
                  />
                  <p className="text-[10px] text-zinc-400 mt-1 font-mono">
                    Default 24 (sekali sehari). Min 1 jam, max 720 jam (30 hari).
                  </p>
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Last Run</Label>
                  <div className="mt-1 h-8 flex items-center text-xs font-mono text-zinc-700">
                    {autoPurge.config.last_run_at
                      ? formatWIB(autoPurge.config.last_run_at)
                      : <span className="text-zinc-400">Belum pernah dijalankan</span>}
                  </div>
                  {autoPurge.config.last_total_deleted != null && (
                    <p className="text-[10px] text-zinc-500 mt-1 font-mono">
                      Terakhir hapus: {fmtInt(autoPurge.config.last_total_deleted)} dokumen
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t border-zinc-100 pt-3">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">
                  Retensi per Koleksi (hari)
                </p>
                <p className="text-[10px] text-zinc-400 mb-2">
                  0 = skip koleksi ini (tidak ikut auto-purge). Dokumen lebih tua dari N hari akan dihapus otomatis.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {autoPurge.purgeable_collections.map(pc => (
                    <div key={pc.key} className="flex items-center justify-between gap-2 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2">
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-zinc-900">{pc.label}</div>
                        <div className="text-[10px] font-mono text-zinc-400">{pc.key}</div>
                      </div>
                      <Input
                        data-testid={`auto-purge-retention-${pc.key}`}
                        type="number"
                        min="0"
                        max="3650"
                        value={autoPurgeForm.retention_days[pc.key] ?? 0}
                        onChange={(e) => setAutoPurgeForm(f => ({
                          ...f,
                          retention_days: { ...f.retention_days, [pc.key]: e.target.value }
                        }))}
                        className="h-7 w-20 text-xs font-mono text-right rounded-sm"
                      />
                      <span className="text-[10px] text-zinc-400">hari</span>
                    </div>
                  ))}
                </div>
              </div>

              {autoPurge.config.last_run_summary?.length > 0 && (
                <details className="border border-zinc-200 rounded-sm">
                  <summary className="px-3 py-2 bg-zinc-50 text-[11px] font-semibold uppercase tracking-widest text-zinc-600 cursor-pointer">
                    Ringkasan Run Terakhir ({autoPurge.config.last_run_summary.length} koleksi)
                  </summary>
                  <table className="w-full text-xs">
                    <tbody>
                      {autoPurge.config.last_run_summary.map((s, i) => (
                        <tr key={i} className="border-t border-zinc-100">
                          <td className="px-3 py-1.5 text-zinc-700">{s.label}</td>
                          <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-500 text-right">
                            retensi {s.retention_days}h
                          </td>
                          <td className="px-3 py-1.5 text-[10px] font-mono text-right">
                            {s.skipped
                              ? <span className="text-zinc-400">skip</span>
                              : <span className="text-red-600">−{fmtInt(s.deleted)}</span>}
                          </td>
                          <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-500 text-right">
                            {s.remaining != null ? `sisa ${fmtInt(s.remaining)}` : (s.error || "")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}

              <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3">
                <Button
                  data-testid="auto-purge-run-now-btn"
                  variant="outline"
                  onClick={runAutoPurgeNow}
                  disabled={runningAutoPurge || !autoPurgeForm.enabled}
                  className="rounded-sm gap-1 text-xs h-8 border-amber-300 text-amber-800 hover:bg-amber-50">
                  <Broom size={14} /> {runningAutoPurge ? "Menjalankan..." : "Run Sekarang"}
                </Button>
                <Button
                  data-testid="auto-purge-save-btn"
                  onClick={saveAutoPurge}
                  disabled={savingAutoPurge}
                  className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-1 text-xs h-8">
                  {savingAutoPurge ? "Menyimpan..." : "Simpan Config"}
                </Button>
              </div>

              {autoPurge.config.updated_at && (
                <p className="text-[10px] text-zinc-400 font-mono text-right">
                  Config diupdate: {formatWIB(autoPurge.config.updated_at)}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Purge Confirmation Dialog */}
      <Dialog open={!!purgeTarget} onOpenChange={(v) => !v && setPurgeTarget(null)}>
        <DialogContent data-testid="purge-dialog" className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Trash size={18} className="text-red-600" />
              Reset {purgeTarget?.label}
            </DialogTitle>
          </DialogHeader>
          {purgeTarget && (
            <div className="space-y-3 text-xs">
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Koleksi</span>
                  <span className="font-mono font-semibold">{purgeTarget.collection}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-zinc-500">Dokumen saat ini</span>
                  <span className="font-mono font-semibold">{fmtInt(purgeTarget.count)}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-zinc-500">Ukuran</span>
                  <span className="font-mono font-semibold">{purgeTarget.size_mb} MB</span>
                </div>
              </div>

              <div>
                <Label className="text-[11px] text-zinc-600">Hapus yang lebih tua dari (hari)</Label>
                <select
                  data-testid="purge-older-than-select"
                  value={purgeForm.older_than_days}
                  onChange={(e) => setPurgeForm(f => ({ ...f, older_than_days: Number(e.target.value) }))}
                  className="mt-1 w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-xs bg-white outline-none">
                  <option value="0">Semua (HAPUS TOTAL)</option>
                  <option value="1">1 hari</option>
                  <option value="7">7 hari</option>
                  <option value="14">14 hari</option>
                  <option value="30">30 hari</option>
                  <option value="60">60 hari</option>
                  <option value="90">90 hari</option>
                </select>
              </div>

              {purgeTarget.key === "ai_call_logs" && (
                <>
                  <div>
                    <Label className="text-[11px] text-zinc-600">Filter Feature (opsional)</Label>
                    <select
                      data-testid="purge-feature-select"
                      value={purgeForm.feature}
                      onChange={(e) => setPurgeForm(f => ({ ...f, feature: e.target.value }))}
                      className="mt-1 w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-xs bg-white outline-none">
                      <option value="">Semua feature</option>
                      <option value="deep_analyze">deep_analyze</option>
                      <option value="ai_assistant">ai_assistant</option>
                      <option value="briefing">briefing</option>
                      <option value="suggestions">suggestions</option>
                      <option value="job_summary">job_summary</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-600">Filter Status (opsional)</Label>
                    <select
                      data-testid="purge-status-select"
                      value={purgeForm.status}
                      onChange={(e) => setPurgeForm(f => ({ ...f, status: e.target.value }))}
                      className="mt-1 w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-xs bg-white outline-none">
                      <option value="">Semua status</option>
                      <option value="success">success</option>
                      <option value="error">error</option>
                      <option value="rate_limited">rate_limited</option>
                      <option value="timeout">timeout</option>
                      <option value="budget_blocked">budget_blocked</option>
                    </select>
                  </div>
                </>
              )}

              <div className="bg-red-50 border border-red-200 rounded-sm p-2 text-[11px] text-red-700">
                <strong>Peringatan:</strong> Aksi ini tidak bisa di-undo. Sistem akan meminta
                Anda mengetik ulang nama koleksi untuk konfirmasi final.
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
                <Button
                  variant="outline"
                  onClick={() => setPurgeTarget(null)}
                  disabled={purging}
                  className="rounded-sm text-xs h-8">
                  Batal
                </Button>
                <Button
                  data-testid="purge-confirm-btn"
                  onClick={runPurge}
                  disabled={purging}
                  className="bg-red-600 text-white hover:bg-red-700 rounded-sm gap-1 text-xs h-8">
                  <Trash size={12} /> {purging ? "Menghapus..." : "Reset Sekarang"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Log Detail Dialog */}
      <Dialog open={!!logDetail} onOpenChange={(v) => !v && setLogDetail(null)}>
        <DialogContent className="rounded-sm max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {logDetail?.feature && (
                <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${FEATURE_COLOR[logDetail.feature] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
                  {logDetail.feature}
                </span>
              )}
              Detail AI Call
              {logDetail?.status && (
                <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm ${STATUS_COLOR[logDetail.status] || "bg-zinc-100 text-zinc-700"}`}>
                  {logDetail.status}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto space-y-3 mt-2 pr-2">
            {logDetail?._loading && loadingLogDetail && (
              <p className="text-xs text-zinc-500">Memuat detail...</p>
            )}
            {logDetail?.error && (
              <p className="text-xs text-red-600 bg-red-50 p-2 rounded-sm">{logDetail.error}</p>
            )}
            {logDetail && !logDetail._loading && (
              <>
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <LogMeta label="Waktu" value={logDetail.created_at ? formatWIB(logDetail.created_at) : "-"} />
                  <LogMeta label="Model" value={logDetail.model} mono />
                  <LogMeta label="Tokens (in / out / total)" value={`${fmtInt(logDetail.input_tokens)} / ${fmtInt(logDetail.output_tokens)} / ${fmtInt(logDetail.total_tokens)}`} mono />
                  <LogMeta label="Latency" value={fmtMs(logDetail.latency_ms)} mono />
                  <LogMeta label="Cost" value={fmtUSD(logDetail.cost_usd, 6)} mono />
                  {logDetail.job_id && <LogMeta label="Job ID" value={logDetail.job_id} mono />}
                  {logDetail.prompt_version && <LogMeta label="Prompt Version" value={logDetail.prompt_version} mono />}
                  {logDetail.user_email && <LogMeta label="User" value={logDetail.user_email} />}
                  {logDetail.cache_hit != null && <LogMeta label="Cache Hit" value={logDetail.cache_hit ? "Yes" : "No"} />}
                </div>

                {/* Extra info (tools_used for ai_assistant, etc) */}
                {logDetail.extra && Object.keys(logDetail.extra).length > 0 && (
                  <details className="border border-zinc-200 rounded-sm">
                    <summary className="px-3 py-2 bg-zinc-50 text-[11px] font-semibold uppercase tracking-widest text-zinc-600 cursor-pointer">
                      Extra Metadata
                    </summary>
                    <pre className="text-[10px] font-mono text-zinc-700 p-3 overflow-x-auto">
                      {JSON.stringify(logDetail.extra, null, 2)}
                    </pre>
                  </details>
                )}

                {/* Error detail */}
                {logDetail.error && logDetail.status !== "success" && (
                  <div className="border border-red-200 rounded-sm bg-red-50">
                    <div className="px-3 py-2 border-b border-red-200 text-[11px] font-semibold uppercase tracking-widest text-red-700">
                      Error
                    </div>
                    <p className="p-3 text-xs font-mono text-red-800 whitespace-pre-wrap">{logDetail.error}</p>
                  </div>
                )}

                {/* Session excerpt for AI Assistant */}
                {logDetail.session_excerpt && (
                  <div className="border border-blue-200 rounded-sm">
                    <div className="px-3 py-2 bg-blue-50 border-b border-blue-200 text-[11px] font-semibold uppercase tracking-widest text-blue-700 flex items-center justify-between">
                      <span>Konteks Sesi (latest exchange)</span>
                      <span className="font-mono normal-case text-[9px] text-zinc-500">
                        Session {logDetail.session_excerpt.session_id?.slice(0, 8)} · {logDetail.session_excerpt.total_messages} total messages
                      </span>
                    </div>
                    <div className="p-3 space-y-2">
                      {logDetail.session_excerpt.latest_exchange.map((m, i) => (
                        <div key={i} className={`text-xs p-2 rounded-sm ${
                          m.role === "user" ? "bg-zinc-100" : "bg-blue-50 border border-blue-100"
                        }`}>
                          <p className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                            {m.role}
                            {m.timestamp && <span className="ml-2 normal-case">{formatWIBTime(m.timestamp)}</span>}
                          </p>
                          <p className="whitespace-pre-wrap text-zinc-800">{(m.content || "").slice(0, 1000)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input Prompt */}
                {logDetail.prompt_preview ? (
                  <div className="border border-zinc-200 rounded-sm">
                    <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                      <span className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600">Input Prompt</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-mono text-zinc-400">{logDetail.prompt_preview.length.toLocaleString()} chars</span>
                        <CopyButton text={logDetail.prompt_preview} label="prompt" />
                      </div>
                    </div>
                    <pre className="text-[10px] font-mono text-zinc-800 p-3 max-h-96 overflow-auto whitespace-pre-wrap">{logDetail.prompt_preview}</pre>
                  </div>
                ) : (logDetail.feature !== "suggestions" && logDetail.status === "success" && (
                  <div className="border border-dashed border-zinc-300 rounded-sm p-3 text-[11px] text-zinc-500">
                    <strong>Prompt tidak tersimpan</strong> — log ini dibuat sebelum fitur detail diaktifkan.
                    Call AI berikutnya akan menyimpan prompt & response untuk di-review.
                  </div>
                ))}

                {/* Output Response */}
                {logDetail.response_preview && (
                  <div className="border border-emerald-200 rounded-sm">
                    <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between">
                      <span className="text-[11px] font-semibold uppercase tracking-widest text-emerald-700">LLM Response</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-mono text-zinc-400">{logDetail.response_preview.length.toLocaleString()} chars</span>
                        <CopyButton text={logDetail.response_preview} label="response" />
                      </div>
                    </div>
                    <pre className="text-[10px] font-mono text-zinc-800 p-3 max-h-96 overflow-auto whitespace-pre-wrap">{logDetail.response_preview}</pre>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-zinc-100 gap-2">
            <div className="flex items-center gap-2">
              {/* Re-run with different model — only for job-based AI features */}
              {logDetail && !logDetail._loading && logDetail.job_id &&
               ["deep_analyze", "job_summary"].includes(logDetail.feature) && (
                <Button
                  data-testid="rerun-in-benchmark-btn"
                  onClick={() => {
                    setBenchForm(f => ({ ...f, job_id: logDetail.job_id }));
                    setTab("benchmark");
                    setLogDetail(null);
                    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
                  }}
                  className="bg-amber-500 text-white hover:bg-amber-600 rounded-sm gap-1 text-xs h-8">
                  <Lightning size={12} weight="fill" />
                  Re-run with Different Model
                </Button>
              )}
              {/* Re-send for ai_assistant — navigate to assistant page */}
              {logDetail && !logDetail._loading && logDetail.feature === "ai_assistant" && logDetail.prompt_preview && (
                <Button
                  data-testid="resend-to-assistant-btn"
                  variant="outline"
                  onClick={() => {
                    // Copy to clipboard + navigate
                    try { navigator.clipboard.writeText(logDetail.prompt_preview); } catch (e) { /* ignore */ }
                    window.open("/ai-assistant", "_blank");
                  }}
                  className="rounded-sm gap-1 text-xs h-8">
                  <Robot size={12} />
                  Buka AI Asisten (prompt disalin)
                </Button>
              )}
            </div>
            <Button variant="outline" onClick={() => setLogDetail(null)} className="rounded-sm text-xs h-8">Tutup</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Budget Config Dialog */}
      <Dialog open={showBudget} onOpenChange={setShowBudget}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Budget Configuration</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Daily Cap (USD)</Label>
              <Input data-testid="budget-daily-input" type="number" step="0.01" min="0"
                value={budgetForm.daily_cap_usd}
                onChange={(e) => setBudgetForm({ ...budgetForm, daily_cap_usd: Number(e.target.value) })}
                className="rounded-sm border-zinc-300 font-mono" />
              <p className="text-[10px] text-zinc-400">Setel 0 untuk menonaktifkan daily cap</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Monthly Cap (USD)</Label>
              <Input data-testid="budget-monthly-input" type="number" step="0.01" min="0"
                value={budgetForm.monthly_cap_usd}
                onChange={(e) => setBudgetForm({ ...budgetForm, monthly_cap_usd: Number(e.target.value) })}
                className="rounded-sm border-zinc-300 font-mono" />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input data-testid="budget-hardcap-toggle" type="checkbox" checked={budgetForm.hard_cap_enabled}
                onChange={(e) => setBudgetForm({ ...budgetForm, hard_cap_enabled: e.target.checked })} />
              <span>Aktifkan hard cap (reject AI calls bila budget terlampaui)</span>
            </label>
            <div className="border border-amber-200 bg-amber-50 rounded-sm p-2 text-[11px] text-amber-900 flex items-start gap-2">
              <Warning size={14} className="mt-0.5 flex-shrink-0" />
              <span>Saat hard cap aktif dan budget terlampaui, fitur AI akan menolak permintaan. Operator akan melihat pesan error yang mengarahkan mereka ke halaman ini.</span>
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="save-budget-btn" onClick={saveBudget} disabled={savingBudget}
                className="flex-1 bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm">
                {savingBudget ? "Menyimpan..." : "Simpan"}
              </Button>
              <Button variant="outline" onClick={() => setShowBudget(false)} className="rounded-sm">Batal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-components ───

function KpiCard({ label, value, sub, accent = "zinc", progress, testId }) {
  const accents = {
    zinc: "border-zinc-200",
    violet: "border-violet-200",
    blue: "border-blue-200",
    emerald: "border-emerald-200",
    amber: "border-amber-200",
    red: "border-red-200",
  };
  const valueColor = {
    zinc: "text-zinc-900",
    violet: "text-violet-700",
    blue: "text-blue-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-red-700",
  };
  return (
    <div data-testid={testId} className={`border ${accents[accent]} rounded-sm p-4 bg-white`}>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</p>
      <p className={`text-xl font-mono font-semibold mt-1 ${valueColor[accent]}`}>{value}</p>
      {sub && <p className="text-[10px] text-zinc-500 mt-1 font-mono">{sub}</p>}
      {progress != null && (
        <div className="h-1 rounded-sm bg-zinc-100 overflow-hidden mt-2">
          <div className={`h-full transition-all ${progress >= 80 ? "bg-red-500" : progress >= 50 ? "bg-amber-500" : "bg-emerald-500"}`}
            style={{ width: `${Math.min(100, progress)}%` }} />
        </div>
      )}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between items-baseline text-xs">
      <span className="text-zinc-500 font-mono">{label}</span>
      <span className={`font-mono ${bold ? "font-semibold text-zinc-900" : "text-zinc-700"}`}>{value}</span>
    </div>
  );
}

function CostTimeSeriesChart({ data }) {
  const max = Math.max(...data.series.map(d => d.total_cost), 0.0001);
  return (
    <div data-testid="cost-chart" className="border border-zinc-200 rounded-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600">Cost Trend (30 hari)</h3>
        <span className="text-[10px] font-mono text-zinc-500">Max: {fmtUSD(max, 4)}</span>
      </div>
      <div className="flex items-end gap-1 h-32">
        {data.series.map((d, i) => {
          const pct = (d.total_cost / max) * 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div className="w-full rounded-sm bg-violet-200 hover:bg-violet-400 transition-colors"
                style={{ height: `${Math.max(1, pct)}%` }} />
              <span className="text-[8px] font-mono text-zinc-400">{d.date.slice(-2)}</span>
              <div className="absolute bottom-full mb-1 hidden group-hover:block bg-zinc-900 text-white text-[10px] px-2 py-1 rounded-sm whitespace-nowrap z-10 font-mono">
                {d.date}: {fmtUSD(d.total_cost, 4)} ({d.total_calls} calls)
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModelSelect({ value, onChange, catalog, allowDefault = false, testId }) {
  return (
    <select
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-xs bg-white outline-none font-mono"
    >
      {allowDefault && <option value="">Gunakan default</option>}
      {Object.entries(catalog || {}).map(([provider, models]) => (
        <optgroup key={provider} label={provider.toUpperCase()}>
          {models.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}


function BenchmarkResult({ benchmark }) {
  const { status, results = [], agreement = {}, winner, total_cost_usd, job_id } = benchmark;

  if (status === "queued" || status === "running") {
    return (
      <div data-testid="bench-progress" className="border border-blue-200 bg-blue-50 rounded-sm p-4">
        <div className="flex items-center gap-3">
          <ArrowClockwise size={16} className="animate-spin text-blue-600" />
          <div>
            <p className="text-sm font-semibold text-blue-900">Benchmark sedang berjalan...</p>
            <p className="text-[11px] text-blue-700">Memanggil {benchmark.models?.length || 0} model untuk job {job_id?.slice(0, 8)}. Ini bisa memakan 30-120 detik.</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div data-testid="bench-error" className="border border-red-200 bg-red-50 rounded-sm p-4">
        <p className="text-sm font-semibold text-red-900">Benchmark gagal</p>
        <p className="text-xs text-red-700 mt-1 font-mono">{benchmark.error || "Unknown error"}</p>
      </div>
    );
  }

  // Detect the "no-needles skipped" case (all models 0 verdicts + 0 cost + 0 tokens)
  const allEmpty = results.length > 0 && results.every(r =>
    !r.error && (r.verdict_count || 0) === 0 && (r.cost_usd || 0) === 0 &&
    (r.input_tokens || 0) === 0 && (r.output_tokens || 0) === 0
  );
  if (allEmpty) {
    return (
      <div data-testid="bench-skipped" className="border border-amber-200 bg-amber-50 rounded-sm p-4">
        <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
          <Warning size={16} weight="fill" /> Benchmark tidak bermakna — Job tidak punya pending items
        </p>
        <p className="text-xs text-amber-800 mt-1.5 leading-relaxed">
          Job <strong className="font-mono">{job_id?.slice(0, 8)}</strong> ini sudah 100% di-match oleh deterministic matcher — tidak ada Saldo/Pulsa/Suntik/Outflow yang pending untuk dianalisis AI.
          Karena tidak ada LLM call yang benar-benar dilakukan, semua metrik (cost, tokens, verdicts) adalah 0.
          <br /><br />
          <strong>Solusi:</strong> Pilih job lain yang masih punya pending items (lihat section "Analisa Mendalam" di halaman Job Detail — harus menampilkan jumlah needles &gt; 0).
        </p>
      </div>
    );
  }

  const validResults = results.filter(r => !r.error);
  const maxCost = Math.max(...validResults.map(r => r.cost_usd || 0), 0.0001);
  const maxLatency = Math.max(...validResults.map(r => r.latency_ms || 0), 1);

  return (
    <div data-testid="bench-result" className="space-y-4">
      {/* Summary banner */}
      <div className="border border-emerald-200 bg-emerald-50/60 rounded-sm p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
              <Trophy size={16} weight="fill" className="text-emerald-600" />
              Winner (Cost-Efficiency): <span className="font-mono">{winner || "—"}</span>
            </h3>
            <p className="text-xs text-emerald-700 mt-1">
              Total biaya benchmark: <strong>{fmtUSD(total_cost_usd, 4)}</strong>
              {agreement.total_needles > 0 && (
                <span className="ml-3">
                  · Agreement rate: <strong>{fmtPct(agreement.agreement_rate)}</strong> ({agreement.total_needles} needles)
                  · Hard agreement (citation sama): <strong>{fmtPct(agreement.hard_agreement_rate)}</strong>
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Per-model comparison */}
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(results.length, 4)}, minmax(0, 1fr))` }}>
        {results.map((r, i) => (
          <div key={r.model} className={`border rounded-sm p-4 ${r.error ? "border-red-200 bg-red-50" : r.model === winner ? "border-emerald-300 bg-emerald-50/40" : "border-zinc-200 bg-white"}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-mono font-semibold">{r.model}</p>
              {r.model === winner && <Trophy size={12} weight="fill" className="text-emerald-600" />}
            </div>
            {r.error ? (
              <p className="text-xs text-red-700 font-mono">{r.error}</p>
            ) : (
              <div className="space-y-1.5">
                <BenchRow label="Verdicts" value={r.verdict_count} />
                <BenchRow label="Cost" value={fmtUSD(r.cost_usd, 4)} bar={(r.cost_usd / maxCost) * 100} barColor="bg-amber-400" />
                <BenchRow label="Latency" value={fmtMs(r.latency_ms)} bar={(r.latency_ms / maxLatency) * 100} barColor="bg-blue-400" />
                <BenchRow label="Tokens (in/out)" value={`${fmtInt(r.input_tokens)} / ${fmtInt(r.output_tokens)}`} />
                {r.avg_confidence > 0 && <BenchRow label="Avg Conf" value={r.avg_confidence.toFixed(2)} />}
                {r.decision_counts && (
                  <div className="pt-2 border-t border-zinc-100 space-y-0.5">
                    <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Decisions</p>
                    <div className="text-[10px] font-mono space-y-0.5">
                      {Object.entries(r.decision_counts).map(([k, v]) => v > 0 && (
                        <div key={k} className="flex justify-between">
                          <span className={
                            k === "MATCH_FOUND" ? "text-emerald-700"
                            : k === "NO_MATCH_CONFIRMED" ? "text-zinc-500"
                            : k === "NEEDS_HUMAN_REVIEW" ? "text-amber-700" : "text-zinc-600"
                          }>{k}</span>
                          <span className="font-semibold">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Per-needle agreement table */}
      {agreement.per_needle && agreement.per_needle.length > 0 && (
        <div className="border border-zinc-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600">Per-Needle Agreement ({agreement.per_needle.length})</h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Needle</th>
                {results.map(r => (
                  <th key={r.model} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 font-mono">{r.model}</th>
                ))}
                <th className="text-center px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Agreed</th>
              </tr>
            </thead>
            <tbody>
              {agreement.per_needle.map((n, i) => (
                <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-3 py-2 text-[10px] font-mono text-zinc-700">{n.key}</td>
                  {results.map(r => {
                    const d = n.decisions[r.model] || "—";
                    const color =
                      d === "MATCH_FOUND" ? "bg-emerald-100 text-emerald-700"
                      : d === "NO_MATCH_CONFIRMED" ? "bg-zinc-100 text-zinc-700"
                      : d === "NEEDS_HUMAN_REVIEW" ? "bg-amber-100 text-amber-700"
                      : "bg-red-100 text-red-700";
                    return (
                      <td key={r.model} className="px-3 py-2">
                        <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm ${color}`}>{d}</span>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-center">
                    {n.hard_agreed ? (
                      <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm bg-emerald-200 text-emerald-900">HARD ✓</span>
                    ) : n.agreed ? (
                      <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">✓</span>
                    ) : (
                      <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm bg-red-100 text-red-700">✗</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BenchRow({ label, value, bar, barColor = "bg-zinc-400" }) {
  return (
    <div>
      <div className="flex justify-between items-baseline text-xs">
        <span className="text-zinc-500 text-[10px] font-mono uppercase tracking-widest">{label}</span>
        <span className="font-mono text-zinc-800 font-semibold">{value}</span>
      </div>
      {bar != null && (
        <div className="h-1 bg-zinc-100 rounded-sm overflow-hidden mt-1">
          <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, bar)}%` }} />
        </div>
      )}
    </div>
  );
}


function LogMeta({ label, value, mono }) {
  return (
    <div className="border border-zinc-100 rounded-sm p-2">
      <p className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{label}</p>
      <p className={`text-xs text-zinc-800 mt-0.5 ${mono ? "font-mono" : ""} break-all`}>{value || "-"}</p>
    </div>
  );
}


function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Fallback: create textarea & exec
      const ta = document.createElement("textarea");
      ta.value = text || "";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1500); }
      catch (_) { /* ignore */ }
      document.body.removeChild(ta);
    }
  };
  return (
    <button
      data-testid={`copy-${label}-btn`}
      onClick={handleCopy}
      className={`text-[10px] font-mono flex items-center gap-1 px-2 py-0.5 rounded-sm border transition-colors ${
        copied ? "bg-emerald-100 text-emerald-700 border-emerald-200"
        : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"
      }`}
      title={`Salin ${label}`}
    >
      {copied ? <><Check size={10} weight="bold" /> Tersalin</> : <><Copy size={10} /> Salin</>}
    </button>
  );
}

